import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  getCategoryDetailQuestions,
  getSendMasterDataChangeTicketActionState,
  getSendMasterDataChangeTicketResponse,
  IHierarchicalQuestionTicketOverview,
  IMasterdataChangeTicket,
  IRootQuestionTicketOverview,
  LoadCategoryDetailQuestions,
  MasterdataChangeType,
  masterDataSelectionTranslateKeys,
  ScoreQuestionType,
  SendMasterDataChangeTicket,
  TicketIssueType,
} from '@resident-nx/shared';
import { FormArray, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { SideSheetContentDirective } from '../../../organisms/side-sheet-form/directives/side-sheet-content.directive';
import { SideSheetFormComponent } from '../../../organisms/side-sheet-form/side-sheet-form.component';
import { TranslateModule } from '@ngx-translate/core';
import { FileListPipe } from '../../../../pipes/file-list.pipe';
import { Store } from '@ngrx/store';
import { TicketDetailQuestionsComponent } from '../../../organisms/ticketing/ticket-creation/ticket-detail-questions/ticket-detail-questions.component';
import { delay, filter, map, tap } from 'rxjs';
import { TicketHelperService } from '../../../../services/tickets/ticket-helper.service';
import { UserProfileEditSummaryComponent } from '../user-profile-edit/user-profile-edit-summary/user-profile-edit-summary.component';
import { UserProfileEditSuccessComponent } from '../user-profile-edit/user-profile-edit-success/user-profile-edit-success.component';
import { UserProfileEditErrorComponent } from '../user-profile-edit/user-profile-edit-error/user-profile-edit-error.component';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { PdfAttachmentComponent } from '../../pdf-attachment/pdf-attachment.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ImageBorderRadiusEnum } from '@resident-nx/web/util';

interface SummaryItem {
  key: string;
  type: ScoreQuestionType;
  // eslint-disable-next-line
  value: any;
}

@Component({
  standalone: true,
  selector: 'rs-web-user-profile-edit-by-ticket',
  imports: [
    ReactiveFormsModule,
    SideSheetContentDirective,
    SideSheetFormComponent,
    TranslateModule,
    FileListPipe,
    TicketDetailQuestionsComponent,
    UserProfileEditSummaryComponent,
    UserProfileEditSuccessComponent,
    UserProfileEditErrorComponent,
    PdfAttachmentComponent,
    AsyncPipe,
    DatePipe,
  ],
  templateUrl: './user-profile-edit-by-ticket.component.html',
  styleUrl: './user-profile-edit-by-ticket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileEditByTicketComponent implements OnInit {
  public ticketCategoryId: string; // injected by SidesheetService.open()
  public changeType: MasterdataChangeType; // injected by SidesheetService.open()
  public submitted = signal(false);
  public showError = computed(() => this.submitted() && this.response() === false);
  public showSuccess = computed(() => this.submitted() && !!this.response());
  public summary = signal<SummaryItem[]>([]);
  public sidesheetFormComponent = viewChild(SideSheetFormComponent);
  public title = computed(() =>
    this.changeType === MasterdataChangeType.PAYMENT
      ? 'user_payment_edit_page_heading'
      : 'user_edit_heading'
  );
  public doneButtonText = computed(() => {
    if (this.showSuccess()) {
      return 'general.close';
    } else if (this.showError()) {
      return 'back_to_overview_l';
    } else {
      return 'submit_l';
    }
  });
  protected readonly ScoreQuestionType = ScoreQuestionType;
  protected readonly ImageBorderRadiusEnum = ImageBorderRadiusEnum;
  protected readonly selectionTranslateKeys = masterDataSelectionTranslateKeys;
  protected readonly TicketIssueType = TicketIssueType;
  #store = inject(Store);
  public response = this.#store.selectSignal(getSendMasterDataChangeTicketResponse);
  public masterDataChangeTicketActionState = this.#store.selectSignal(
    getSendMasterDataChangeTicketActionState
  );
  public rootQuestion$ = this.#store.select(getCategoryDetailQuestions).pipe(
    delay(10), // prevent expression changed after checked error
    filter(rootQuestion => !!rootQuestion),
    map(rootQuestion => this.getSortedQuestions(rootQuestion)),
    tap(rootQuestion => {
      if (!rootQuestion) {
        return;
      }

      this.createDetailQuestionsFormArray(rootQuestion.questions);
    })
  );
  public rootQuestion = toSignal(this.rootQuestion$);

  #fb = inject(FormBuilder);
  public form = this.#fb.group({
    detailQuestions: this.#fb.array([]),
  });
  #ticketHelperService = inject(TicketHelperService);
  #sidesheetInstance = inject(NgbActiveOffcanvas);

  get detailQuestionsFormArray(): FormArray<FormControl | FormArray<FormControl>> {
    return this.form.controls.detailQuestions as FormArray<FormControl | FormArray<FormControl>>;
  }

  ngOnInit(): void {
    const { ticketCategoryId } = this;
    if (ticketCategoryId) {
      this.#store.dispatch(LoadCategoryDetailQuestions({ ticketCategoryId }));
    }
  }

  public async questionStepDone(): Promise<void> {
    this.summary.set(this.createSummary());
    this.sidesheetFormComponent().nextTab();
  }

  public lastStepDone(): void {
    if (this.showSuccess()) {
      this.closeSidesheet();
    } else if (this.showError()) {
      this.submitted.set(false);
    } else {
      void this.createTicket();
    }
  }

  public closeSidesheet(): void {
    this.#sidesheetInstance.dismiss();
  }

  private async createTicket(): Promise<void> {
    const payload = await this.createPayloadForTicketCreationRequest();
    this.submitted.set(true);
    this.#store.dispatch(SendMasterDataChangeTicket({ payload }));
  }

  private createDetailQuestionsFormArray(questions: IHierarchicalQuestionTicketOverview[]): void {
    if (!questions) {
      return;
    }

    this.detailQuestionsFormArray.clear();
    for (const question of questions) {
      const control = this.#ticketHelperService.createDetailQuestionsControl(question);
      this.detailQuestionsFormArray.push(control);
    }
  }

  private createSummary(): SummaryItem[] {
    return this.rootQuestion().questions.map((question, index) => {
      let value = this.detailQuestionsFormArray.at(index).value;
      if (question.data.type === ScoreQuestionType.MULTISELECT) {
        value = this.#ticketHelperService.getCheckedMultiselectValues(question, value);
      }
      return {
        key: question.data.title,
        type: question.data.type,
        value,
      };
    });
  }

  private async createPayloadForTicketCreationRequest(): Promise<IMasterdataChangeTicket> {
    const { ticketCategoryId } = this;
    const formResponses = await this.#ticketHelperService.createFormResponseCreates(
      this.rootQuestion().questions,
      this.detailQuestionsFormArray
    );
    return {
      formResponses,
      ticketCategoryId,
    };
  }

  private getSortedQuestions(
    rootQuestion: IRootQuestionTicketOverview
  ): IRootQuestionTicketOverview {
    const questionsWithOrderedAnswers = rootQuestion.questions.map(question => {
      const sortedAnswers = question.answers.toSorted((a, b) => a.data.order - b.data.order);
      return { ...question, answers: sortedAnswers };
    });

    const orderedQuestions = questionsWithOrderedAnswers.toSorted(
      (a, b) => a.data.order - b.data.order
    );
    return {
      ...rootQuestion,
      questions: orderedQuestions,
    };
  }
}
